<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import {LogOut, User, FileSliders, LifeBuoy} from 'lucide-vue-next'
import { firebase } from '@/router'
import { API, HOST } from '@/utils/http'

import { Search } from '@/components/filters'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import OrgControl from './OrgControl.vue'

const router = useRouter()
const store = useStore()
const member = computed(() => store.getters['getMember'])

const initials = computed(() => {
  const name = member.value.display_name
  if (!name) {
    return '?'
  }
  const names = name.split(' ')
  const first = names[0].charAt(0)
  const last = names[names.length - 1].charAt(0)
  return first + last
})

const logOutUser = () => {
  store.dispatch('logout')
}

const isLoggedIn = computed(() => {
  return store.getters['getIsLoggedIn']
})

const getZendeskSsoUrl = async () => {
  try {
    const firebaseToken = await firebase.auth.currentUser.getIdToken(true)

    const response = await API({
      method: 'POST',
      url: `${HOST}/zendesk-auth`,
      data: {
        firebase_token: firebaseToken
      }
    })

    return response.data || null
  } catch (error) {
    console.error('Error fetching Zendesk SSO URL:', error);
    return null
  }
}

const handleZendeskLogin = async () => {
  try {
    const zendeskData = await getZendeskSsoUrl()
    if (zendeskData && zendeskData.url && zendeskData.jwt) {
      // Create a form to submit JWT via POST as required by Zendesk
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = zendeskData.url
      form.target = '_blank'

      const jwtInput = document.createElement('input')
      jwtInput.type = 'hidden'
      jwtInput.name = 'jwt'
      jwtInput.value = zendeskData.jwt

      form.appendChild(jwtInput)
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    }
  } catch (error) {
    console.error('Error opening Zendesk Help Centre:', error)
  }
}

const zendeskAvailable = ref(false)

onMounted(async () => {
  if (isLoggedIn.value) {
    const zendeskData = await getZendeskSsoUrl()
    zendeskAvailable.value = !!(zendeskData && zendeskData.url && zendeskData.jwt)
  }
})
</script>

<template>
  <header class="h-full bg-white flex items-center px-1">
    <div class="flex items-center flex-1">
      <OrgControl />
    </div>

    <div v-if="isLoggedIn" class="flex-1 flex justify-center">
      <div class="hidden md:block">
        <Search />
      </div>
    </div>

    <div v-if="isLoggedIn" class="flex items-center justify-end flex-1 gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger class="flex items-center">
          <div class="flex flex-col items-end">
            <span class="mr-2 text-sm font-bold" style="line-height: 1;">{{member.display_name}}</span>
            <span v-if="member.email" class="mr-2 text-xs text-gray-400">{{member.email}}</span>
            <span v-else-if="!member.email && member.phone" class="mr-2 text-xs text-gray-400">{{member.phone}}</span>
          </div>
          <Avatar class="w-9 h-9">
            <AvatarImage :src="`${member.avatar_url}`" :alt="member.display_name" />
            <AvatarFallback>{{initials}}</AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56">
          <DropdownMenuItem @click="router.push('/user')">
            <User class="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem v-if="member.is_super_user" @click="router.push('/admin')">
            <FileSliders class="mr-2 h-4 w-4" />
            <span>Admin Tools</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem v-if="zendeskAvailable" @click="handleZendeskLogin">
            <LifeBuoy class="mr-2 h-4 w-4" />
            <span>Help Centre</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="logOutUser">
            <LogOut class="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
</template>
